import classnames from "classnames";
import moment from "moment";
import React, { useMemo } from "react";
import { formatNumberToMoneyWithCurrencySymbol } from "services/money.service";
import lang from "translations";
import styles from "./sales-report.module.scss";

export const METRICS = [
  { id: "revenue", label: lang.revenue, field: "revenue", money: true },
  { id: "itemsSold", label: lang.itemsSold, field: "itemsSold" },
  { id: "grossMargin", label: lang.grossMargin, field: "grossMargin", money: true },
  { id: "costOfGood", label: lang.costOfGoods, field: "costOfGood", money: true },
  { id: "discount", label: lang.discounts, field: "discount", money: true },
  { id: "voucher", label: lang.vouchers, field: "voucher", money: true },
  { id: "voids", label: lang.voids, field: "voids", money: true },
  { id: "creditFlow", label: lang.creditFlow, special: "creditFlow", money: true },
  { id: "paymentType", label: lang.paymentType, special: "paymentType", money: true },
];

export const VIEW_OPTIONS = [
  { id: "OVER_TIME", label: lang.overTime },
  { id: "LOCATION", label: lang.byLocation },
  { id: "GUEST", label: lang.byGuest },
  { id: "STAFF", label: lang.byStaff },
  { id: "ITEM", label: lang.byItem },
  { id: "PEAK_HOUR", label: lang.peakHours },
  { id: "CATEGORY", label: lang.byCategory },
];

export const TIME_GRAINS = [
  { id: "MONTH", label: lang.month },
  { id: "WEEK", label: lang.week },
  { id: "DAY", label: lang.day },
  { id: "YEAR", label: lang.year },
];

export const PEAK_ALL_FILTER = "PEAK_ALL";

const MAX_GROUPING_COLUMNS = 60;

export const PEAK_FILTER_BY_VALUES = [
  "PEAK_MONDAY",
  "PEAK_TUESDAY",
  "PEAK_WEDNESDAY",
  "PEAK_THURSDAY",
  "PEAK_FRIDAY",
  "PEAK_SATURDAY",
  "PEAK_SUNDAY",
];

export const PEAK_DAYS = [
  { id: PEAK_ALL_FILTER, label: lang.allDays },
  { id: "PEAK_MONDAY", label: lang.monday },
  { id: "PEAK_TUESDAY", label: lang.tuesday },
  { id: "PEAK_WEDNESDAY", label: lang.wednesday },
  { id: "PEAK_THURSDAY", label: lang.thursday },
  { id: "PEAK_FRIDAY", label: lang.friday },
  { id: "PEAK_SATURDAY", label: lang.saturday },
  { id: "PEAK_SUNDAY", label: lang.sunday },
];

const PEAK_WEEKDAY_BY_FILTER = {
  PEAK_MONDAY: 1,
  PEAK_TUESDAY: 2,
  PEAK_WEDNESDAY: 3,
  PEAK_THURSDAY: 4,
  PEAK_FRIDAY: 5,
  PEAK_SATURDAY: 6,
  PEAK_SUNDAY: 7,
};

const metricById = (metricId) => METRICS.find((metric) => metric.id === metricId) || METRICS[0];

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatValue = (value, metric) => {
  if (metric?.money) return formatNumberToMoneyWithCurrencySymbol(toNumber(value));
  return new Intl.NumberFormat().format(toNumber(value));
};

const getRowValue = (row, metric) => toNumber(row?.[metric.field]);

const labelFromRow = (row, index, fallbackLabel = "") => {
  const label =
    row?.label ||
    row?.name ||
    row?.objectName ||
    row?.period ||
    row?.date ||
    row?.day ||
    row?.month ||
    row?.year ||
    row?.time ||
    row?.hour ||
    row?.hourOfDay ||
    row?.locationName ||
    row?.categoryName ||
    row?.productName ||
    row?.productSkuName ||
    row?.itemName ||
    row?.guestName ||
    row?.staffName ||
    row?.paymentMethod ||
    row?.paymentMethodName;

  if (label !== undefined && label !== null && label !== "") return String(label);
  return fallbackLabel;
};

const fallbackLabelForView = (view) => {
  if (view === "CATEGORY") return "Uncategorized";
  return "";
};

const timeFromRow = (row, fallback) => {
  const value =
    row?.startDateTime ||
    row?.dateTime ||
    row?.timestamp ||
    row?.date ||
    row?.day ||
    row?.month ||
    row?.year ||
    fallback;
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
};

const getDetailRows = (details, filterBy) => {
  if (["DAY", "MONTH", "YEAR"].includes(filterBy)) {
    return details?.overTimeDetailResponses || [];
  }
  if (filterBy === PEAK_ALL_FILTER || filterBy?.startsWith("PEAK_")) {
    return details?.peakHourDetailResponses || [];
  }
  return details?.objectDetailResponses || [];
};

const getRangeDates = (dateRange) => {
  const [rawStart, rawEnd] = dateRange || [];
  const startDate = moment.min(moment(rawStart), moment(rawEnd || rawStart)).startOf("day");
  const endDate = moment.max(moment(rawStart), moment(rawEnd || rawStart)).startOf("day");
  if (!startDate.isValid() || !endDate.isValid()) return null;
  return { startDate, endDate };
};

const countColumnsForGrain = (dateRange, grain) => {
  const dates = getRangeDates(dateRange);
  if (!dates) return 0;
  const { startDate, endDate } = dates;
  if (grain === "DAY") return endDate.diff(startDate, "days") + 1;
  if (grain === "WEEK") {
    return endDate.clone().startOf("isoWeek").diff(startDate.clone().startOf("isoWeek"), "weeks") + 1;
  }
  if (grain === "MONTH") {
    return endDate.clone().startOf("month").diff(startDate.clone().startOf("month"), "months") + 1;
  }
  if (grain === "YEAR") {
    return endDate.clone().startOf("year").diff(startDate.clone().startOf("year"), "years") + 1;
  }
  return 0;
};

export const getAvailableTimeGrains = (dateRange) => {
  const dates = getRangeDates(dateRange);
  if (!dates) return TIME_GRAINS.filter((grain) => grain.id !== "YEAR");

  const monthDiff = dates.endDate.diff(dates.startDate, "months", true);
  let grainIds = ["MONTH", "WEEK", "DAY"];
  if (monthDiff > 12) {
    grainIds = ["YEAR", "MONTH"];
  } else if (monthDiff > 2) {
    grainIds = ["MONTH", "WEEK"];
  }

  const grains = grainIds
    .filter((grainId) => countColumnsForGrain(dateRange, grainId) <= MAX_GROUPING_COLUMNS)
    .map((grainId) => TIME_GRAINS.find((grain) => grain.id === grainId))
    .filter(Boolean);
  return grains.length ? grains : TIME_GRAINS.filter((grain) => grain.id === "YEAR");
};

const countPeakDays = (dateRange, peakDay) => {
  const [start, end] = dateRange || [];
  const startDate = moment(start).startOf("day");
  const endDate = moment(end).startOf("day");
  if (!startDate.isValid() || !endDate.isValid() || endDate.isBefore(startDate)) return 1;

  if (peakDay === PEAK_ALL_FILTER) {
    return Math.max(endDate.diff(startDate, "days") + 1, 1);
  }

  const targetWeekday = PEAK_WEEKDAY_BY_FILTER[peakDay];
  if (!targetWeekday) return 1;

  let count = 0;
  const cursor = startDate.clone();
  while (cursor.isSameOrBefore(endDate, "day")) {
    if (cursor.isoWeekday() === targetWeekday) count += 1;
    cursor.add(1, "day");
  }
  return Math.max(count, 1);
};

const peakUnitLabel = (peakDay) => {
  if (peakDay === PEAK_ALL_FILTER) return "day";
  return PEAK_DAYS.find((item) => item.id === peakDay)?.label || "day";
};

const pluralPeakUnit = (unit) => (unit === "day" ? "days" : `${unit}s`);

const hourFromRow = (row) => {
  const value = row?.time ?? row?.hour ?? row?.hourOfDay;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(0, Math.min(23, parsed)) : null;
};

const formatHourLabel = (hour) => {
  const normalized = ((Number(hour) % 24) + 24) % 24;
  const hour12 = normalized % 12 || 12;
  return `${hour12} ${normalized >= 12 ? "PM" : "AM"}`;
};

const aggregatePeakRows = (rows, metric, dayCount) => {
  const buckets = Array.from({ length: 24 }, (_, hour) => ({
    label: formatHourLabel(hour),
    value: 0,
    order: hour,
  }));

  rows.forEach((row) => {
    const hour = hourFromRow(row);
    if (hour === null) return;
    buckets[hour].value += getRowValue(row, metric);
  });

  const divisor = Math.max(dayCount || 1, 1);
  return buckets.map((bucket) => ({
    ...bucket,
    value: bucket.value / divisor,
  }));
};

const aggregateWeekRows = (rows, metric) => {
  const buckets = {};
  rows.forEach((row, index) => {
    const parsed = timeFromRow(row, labelFromRow(row, index));
    const key = parsed ? parsed.clone().startOf("isoWeek").format("YYYY-MM-DD") : labelFromRow(row, index);
    const label = parsed ? parsed.clone().startOf("isoWeek").format("MMM D") : key;
    if (!buckets[key]) {
      buckets[key] = { label, value: 0, order: parsed ? parsed.valueOf() : index };
    }
    buckets[key].value += getRowValue(row, metric);
  });
  return Object.values(buckets).sort((a, b) => a.order - b.order);
};

const normalizeRows = (rows, metric, { fallbackLabel = "", sort = false } = {}) => {
  const normalized = rows.map((row, index) => {
    const nextFallbackLabel =
      typeof fallbackLabel === "function" ? fallbackLabel(row, index) : fallbackLabel;

    return {
      label: labelFromRow(row, index, nextFallbackLabel),
      value: getRowValue(row, metric),
      order: index,
    };
  });
  if (!sort) return normalized;
  return normalized.sort((a, b) => b.value - a.value);
};

const getPaymentRows = (overview) => {
  const rows = overview?.paymentMethodDetailResponses || [];
  return rows
    .map((row, index) => ({
      label: labelFromRow(row, index),
      value: toNumber(row.revenue ?? row.totalSales ?? row.amount ?? row.value),
      order: index,
    }))
    .sort((a, b) => b.value - a.value);
};

const totalRows = (rows) => rows.reduce((sum, row) => sum + toNumber(row.value), 0);

const chartTitle = ({ metric, view }) => {
  if (metric.special === "creditFlow") return lang.creditFlow;
  if (metric.special === "paymentType") return lang.salesByPaymentType;
  if (view === "OVER_TIME") return lang.populate(lang.xOverTime, [metric.label]);
  if (view === "PEAK_HOUR") return lang.populate(lang.xByHourOfDay, [metric.label]);
  return lang.populate(lang.xY, [
    metric.label,
    VIEW_OPTIONS.find((item) => item.id === view)?.label?.toLowerCase() || "",
  ]);
};

const EmptyState = ({ text = lang.noDataMatchesTheseFilters }) => (
  <div className={styles.emptyState}>{text}</div>
);

const LoadingState = () => (
  <div className={styles.loadingState}>
    <div />
    <div />
    <div />
  </div>
);

const ErrorState = ({ text, onRetry }) => (
  <button type="button" className={styles.errorState} onClick={onRetry}>
    {text || "Failed to load Sales Report data. Click to retry."}
  </button>
);

const VerticalBars = ({ rows, metric }) => {
  if (!rows.length || !rows.some((row) => row.value)) return <EmptyState />;
  const max = Math.max(...rows.map((row) => row.value), 1);
  return (
    <div className={styles.verticalBars}>
      {rows.map((row) => (
        <div className={styles.verticalBarItem} key={`${row.label}-${row.order}`}>
          <div className={styles.barValue}>{formatValue(row.value, metric)}</div>
          <div
            className={styles.verticalBar}
            style={{ height: `${Math.max(4, Math.round((row.value / max) * 100))}%` }}
            title={`${row.label}: ${formatValue(row.value, metric)}`}
          />
          <div className={styles.axisLabel}>{row.label}</div>
        </div>
      ))}
    </div>
  );
};

const HorizontalBars = ({ rows, metric }) => {
  if (!rows.length || !rows.some((row) => row.value)) return <EmptyState />;
  const max = Math.max(...rows.map((row) => row.value), 1);
  return (
    <div className={styles.horizontalBars}>
      {rows.map((row) => (
        <div className={styles.horizontalRow} key={`${row.label}-${row.order}`}>
          <div className={styles.horizontalLabel}>{row.label}</div>
          <div className={styles.horizontalTrack}>
            <div
              className={styles.horizontalFill}
              style={{ width: `${Math.max(2, Math.round((row.value / max) * 100))}%` }}
            />
          </div>
          <div className={styles.horizontalValue}>{formatValue(row.value, metric)}</div>
        </div>
      ))}
    </div>
  );
};

const AreaLine = ({ rows, metric }) => {
  if (!rows.length || !rows.some((row) => row.value)) return <EmptyState />;
  const width = 1000;
  const height = 300;
  const bottom = 258;
  const max = Math.max(...rows.map((row) => row.value), 1);
  const x = (index) => 20 + (rows.length <= 1 ? 480 : (index / (rows.length - 1)) * 960);
  const y = (value) => 22 + (1 - value / max) * 218;
  const line = rows.map((row, index) => `${index ? "L" : "M"} ${x(index)} ${y(row.value)}`).join(" ");
  const area = `${line} L ${x(rows.length - 1)} ${bottom} L ${x(0)} ${bottom} Z`;
  const labelStep = Math.ceil(rows.length / 12);

  return (
    <div className={styles.areaLine}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${metric.label} peak hour`}>
        <path d={area} className={styles.areaPath} />
        <path d={line} className={styles.linePath} />
        {rows.map((row, index) => (
          <g key={`${row.label}-${row.order}`}>
            <circle cx={x(index)} cy={y(row.value)} r="4" className={styles.lineDot}>
              <title>{`${row.label}: ${formatValue(row.value, metric)}`}</title>
            </circle>
            {(rows.length <= 14 || index % labelStep === 0) && (
              <text x={x(index)} y="285" textAnchor="middle" className={styles.svgLabel}>
                {row.label}
              </text>
            )}
          </g>
        ))}
      </svg>
    </div>
  );
};

const CreditFlow = ({ overview }) => {
  const flow = overview?.paymentFlowDetailResponse || {};
  const toppedUp = toNumber(flow.toppedUp);
  const spentOnSales = toNumber(flow.spentOnSales);
  const freeCredits = toNumber(flow.freeCredits);
  const unspentCredits = toNumber(flow.unspentCredits);
  const segments = [
    { label: lang.spentOnSales, value: spentOnSales, className: styles.creditSpent },
    { label: lang.freeCredits, value: freeCredits, className: styles.creditFree },
    { label: lang.unspentCredit, value: unspentCredits, className: styles.creditUnspent },
  ];
  const base = Math.max(segments.reduce((sum, item) => sum + item.value, 0), 1);

  if (!toppedUp && !spentOnSales && !freeCredits && !unspentCredits) return <EmptyState />;

  return (
    <div>
      <div className={styles.creditCards}>
        <div>
          <span>{lang.toppedUp}</span>
          <strong>{formatValue(toppedUp, METRICS[0])}</strong>
        </div>
        {segments.map((segment) => (
          <div key={segment.label}>
            <span>{segment.label}</span>
            <strong>{formatValue(segment.value, METRICS[0])}</strong>
          </div>
        ))}
      </div>
      <div className={styles.creditBar}>
        {segments
          .filter((segment) => segment.value > 0)
          .map((segment) => (
            <span
              key={segment.label}
              className={segment.className}
              style={{ width: `${(segment.value / base) * 100}%` }}
            />
          ))}
      </div>
      <div className={styles.legend}>
        {segments.map((segment) => (
          <span key={segment.label}>
            <i className={segment.className} />
            {segment.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const SalesCharts = ({
  activeMetric,
  activeView,
  details,
  detailsError,
  detailsLoading,
  filterBy,
  onMetricChange,
  onPeakDayChange,
  onRetry,
  onTimeGrainChange,
  onViewChange,
  overview,
  overviewError,
  overviewLoading,
  timeGrains = TIME_GRAINS,
  peakDay,
  dateRange,
  timeGrain,
}) => {
  const metric = metricById(activeMetric);
  const peakDayCount = useMemo(
    () => (activeView === "PEAK_HOUR" ? countPeakDays(dateRange, peakDay) : 1),
    [activeView, dateRange, peakDay],
  );
  const rows = useMemo(() => {
    if (metric.special === "paymentType") return getPaymentRows(overview);
    const detailRows = getDetailRows(details, filterBy);
    if (activeView === "PEAK_HOUR") {
      return aggregatePeakRows(detailRows, metric, peakDayCount);
    }
    if (timeGrain === "WEEK" && activeView === "OVER_TIME") {
      return aggregateWeekRows(detailRows, metric);
    }
    return normalizeRows(detailRows, metric, {
      fallbackLabel: fallbackLabelForView(activeView),
      sort: activeView !== "OVER_TIME" && activeView !== "PEAK_HOUR",
    });
  }, [activeView, details, filterBy, metric, overview, peakDayCount, timeGrain]);

  const total = totalRows(rows);
  const title = chartTitle({ metric, view: activeView });
  const peakUnit = peakUnitLabel(peakDay);
  const chartLoading = overviewLoading || (!metric.special && detailsLoading);
  const chartError = metric.special === "creditFlow" || metric.special === "paymentType"
    ? overviewError
    : detailsError;

  return (
    <>
      <div className={styles.metricTabs}>
        {METRICS.map((item) => (
          <button
            key={item.id}
            type="button"
            className={classnames({ [styles.active]: item.id === activeMetric })}
            onClick={() => onMetricChange(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className={styles.chartPanel}>
        <div className={styles.chartHeader}>
          <div>
            <div className={styles.titleRow}>
              <h3>{title}</h3>
              {!metric.special && activeView === "OVER_TIME" && (
                <div className={styles.segmented}>
                  {timeGrains.map((grain) => (
                    <button
                      key={grain.id}
                      type="button"
                      className={classnames({ [styles.active]: grain.id === timeGrain })}
                      onClick={() => onTimeGrainChange(grain.id)}
                    >
                      {grain.label}
                    </button>
                  ))}
                </div>
              )}
              {!metric.special && activeView === "PEAK_HOUR" && (
                <select value={peakDay} onChange={(event) => onPeakDayChange(event.target.value)}>
                  {PEAK_DAYS.map((day) => (
                    <option key={day.id} value={day.id}>
                      {day.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {!metric.special && (
              <div className={styles.chartTotal}>
                {activeView === "PEAK_HOUR" ? (
                  <>
                    Average {peakUnit} <strong>{formatValue(total, metric)}</strong> per {peakUnit} -{" "}
                    {peakDayCount} {pluralPeakUnit(peakUnit)} in range
                  </>
                ) : (
                  <>
                    Total {metric.label.toLowerCase()} <strong>{formatValue(total, metric)}</strong>
                  </>
                )}
              </div>
            )}
          </div>
          {!metric.special && (
            <label className={styles.viewSelector}>
              View
              <select value={activeView} onChange={(event) => onViewChange(event.target.value)}>
                {VIEW_OPTIONS.map((view) => (
                  <option key={view.id} value={view.id}>
                    {view.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>

        {chartLoading ? (
          <LoadingState />
        ) : chartError ? (
          <ErrorState
            text={
              overviewError
                ? "Overview failed to load. Click to retry."
                : "Details failed to load. Click to retry."
            }
            onRetry={onRetry}
          />
        ) : metric.special === "creditFlow" ? (
          <CreditFlow overview={overview} />
        ) : metric.special === "paymentType" ? (
          <HorizontalBars rows={rows} metric={METRICS[0]} />
        ) : activeView === "PEAK_HOUR" ? (
          <AreaLine rows={rows} metric={metric} />
        ) : activeView === "OVER_TIME" ? (
          <VerticalBars rows={rows} metric={metric} />
        ) : (
          <HorizontalBars rows={rows} metric={metric} />
        )}
      </div>
    </>
  );
};

export default SalesCharts;
